import { useMemo } from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { FileText, Globe, BookOpen } from "lucide-react";
import type { Agent } from "@/types/aiToolkit";
import type { Source } from "@shared/schema";

type KbItem = { id: string; title?: string | null };

export interface AgentKnowledgeAttachProps {
  value: Partial<Agent>;
  onChange: (patch: Partial<Agent>) => void;
  sources: Source[];
  kbItems: KbItem[];
  requireTrainingContext: boolean;
  onRequireTrainingContextChange: (v: boolean) => void;
}

export function AgentKnowledgeAttach({
  value,
  onChange,
  sources,
  kbItems,
  requireTrainingContext,
  onRequireTrainingContextChange,
}: AgentKnowledgeAttachProps) {
  const [search, setSearch] = useState("");

  const linkedSourceIds = value.linkedSourceIds ?? [];
  const trainingDocumentIds = value.trainingDocumentIds ?? [];
  const totalCount = linkedSourceIds.length + trainingDocumentIds.length;

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const kbRows = kbItems.map((item) => ({
      kind: "kb" as const,
      id: item.id,
      title: item.title || "Untitled",
      subtitle: "Knowledge base",
    }));
    const sourceRows = sources.map((s) => ({
      kind: "source" as const,
      id: s.id,
      title: s.title || "Untitled",
      subtitle: s.type || "Source",
    }));
    const all = [...kbRows, ...sourceRows];
    if (!q) return all;
    return all.filter(
      (row) =>
        row.title.toLowerCase().includes(q) ||
        row.subtitle.toLowerCase().includes(q)
    );
  }, [search, kbItems, sources]);

  const toggleRow = (kind: "kb" | "source", id: string) => {
    if (kind === "kb") {
      const next = trainingDocumentIds.includes(id)
        ? trainingDocumentIds.filter((x) => x !== id)
        : [...trainingDocumentIds, id];
      onChange({ trainingDocumentIds: next });
    } else {
      const next = linkedSourceIds.includes(id)
        ? linkedSourceIds.filter((x) => x !== id)
        : [...linkedSourceIds, id];
      onChange({ linkedSourceIds: next });
    }
  };

  const isSelected = (kind: "kb" | "source", id: string) =>
    kind === "kb" ? trainingDocumentIds.includes(id) : linkedSourceIds.includes(id);

  return (
    <section id="agent-section-knowledge" className="space-y-3 scroll-mt-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-muted-foreground" />
          <Label className="text-base font-medium">Knowledge</Label>
        </div>
        <Badge variant={totalCount > 0 ? "default" : "outline"}>
          {totalCount} attached
        </Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Ground your agent with sources and knowledge base documents — add a little or a lot.
      </p>

      <div className="flex items-center justify-between gap-3 rounded-md border p-3 min-w-0">
        <div className="min-w-0 flex-1">
          <Label className="text-sm">Require training context</Label>
          <p className="text-xs text-muted-foreground">
            Warn when saving without linked documents
          </p>
        </div>
        <Switch
          className="shrink-0"
          checked={requireTrainingContext}
          onCheckedChange={onRequireTrainingContextChange}
        />
      </div>

      <Input
        className="w-full"
        placeholder="Search sources and documents..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="h-[180px] overflow-y-auto border rounded-md min-w-0 w-full">
        <div className="p-2 space-y-1 min-w-0">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No knowledge items found
            </p>
          ) : (
            filtered.slice(0, 80).map((row) => (
              <div
                key={`${row.kind}-${row.id}`}
                className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer min-w-0"
                onClick={() => toggleRow(row.kind, row.id)}
              >
                <Checkbox
                  className="shrink-0"
                  checked={isSelected(row.kind, row.id)}
                  onCheckedChange={() => toggleRow(row.kind, row.id)}
                />
                {row.kind === "kb" ? (
                  <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : (
                  <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{row.title}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {row.subtitle}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {trainingDocumentIds.length} document(s) · {linkedSourceIds.length} source(s)
      </p>
    </section>
  );
}
